import { ChannelType, type ContactAddress } from 'src/api/generated';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import {
  formatPhoneNumber,
  standardizePhoneNumber,
} from 'src/modules/shared/utils/formatting/phone.util';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { SplitButtonDropdown } from 'src/modules/shared/components/molecules/dropdowns/split-button-dropdown';
import { PiPhoneLight, PiGearSixLight, PiCaretDown } from 'react-icons/pi';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { isValidPhoneNumber } from 'src/modules/shared/utils/validation/phone.util';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';

interface ContactCallButtonProps {
  addresses?: ContactAddress[];
  onCall: (phoneNumber: string) => void;
  onOpenConfig?: () => void;
  isLoading?: boolean;
  size?: ComponentSize;
  split?: boolean;
}

export function ContactCallButton({
  addresses = [],
  onCall,
  onOpenConfig,
  isLoading,
  size = 'sm',
  split = true,
}: ContactCallButtonProps) {
  const phoneAddresses = addresses.filter(
    (addr) => addr.channelType === ChannelType.PHONE
  );
  const hasPhoneNumbers = phoneAddresses.length > 0;

  const getPhoneNumberLabel = (phoneNumber: string) => {
    const number = standardizePhoneNumber(phoneNumber) ?? phoneNumber;
    // TODO: check when implementing i18n
    return `Llamar ${number}`;
  };
  const dropdownItems: DropdownItem[] = [
    ...phoneAddresses.map((address, index) => ({
      content: getPhoneNumberLabel(address.address),
      onClick: () => onCall(address.address),
      disabled: !isValidPhoneNumber(address.address),
      icon: <PiPhoneLight className="w-4 h-4" />,
      separator: index === phoneAddresses.length - 1,
    })),
    ...(onOpenConfig
      ? [
          {
            // TODO: check when implementing i18n
            content: 'Configuración',
            onClick: onOpenConfig,
            icon: <PiGearSixLight className="w-4 h-4" />,
            disabled: true,
          },
        ]
      : []),
  ];

  const defaultPhoneNumber = phoneAddresses[0]?.address ?? '';
  const isDefaultPhoneNumberValid = isValidPhoneNumber(defaultPhoneNumber);

  // TODO: check when implementing i18n
  const callButtonTooltipContent = isDefaultPhoneNumberValid
    ? `Llama a ${formatPhoneNumber(defaultPhoneNumber)}`
    : `El número de teléfono ${defaultPhoneNumber} no es válido. Puedes arreglarlo en el perfil del contacto.`;
  const dropdownButtonTooltipContent = hasPhoneNumbers
    ? 'Opciones de llamada'
    : callButtonTooltipContent;

  return (
    <Tooltip
      content={split ? callButtonTooltipContent : dropdownButtonTooltipContent}
      size="sm"
      placement="top"
      className="group"
    >
      {split ? (
        <SplitButtonDropdown
          icon={<PiPhoneLight className="w-5 h-5" />}
          label=""
          onClick={() => onCall(defaultPhoneNumber)}
          items={dropdownItems}
          size={size}
          className="group"
          mainButtonClassName="group"
          variant="call"
          appearance="soft"
          disabled={(isLoading ?? false) || !hasPhoneNumbers}
          mainButtonDisabled={
            (isLoading ?? false) || !isDefaultPhoneNumberValid
          }
        />
      ) : (
        <MenuDropdown
          items={dropdownItems}
          size={size}
          variant="call"
          appearance="soft"
          disabled={(isLoading ?? false) || !hasPhoneNumbers}
          handlerComponent={
            <Button
              type="button"
              variant="call"
              appearance="soft"
              size={size}
              className="group"
            >
              <PiPhoneLight className="h-5 w-5" />
              <PiCaretDown className="h-4 w-4" />
            </Button>
          }
        />
      )}
    </Tooltip>
  );
}
